import React, { useMemo, useRef, useState } from 'react'
import { Pill } from '../../../../../../shared/sso-workspace/pill'
import { Notice } from '../../../../../../shared/sso-workspace/fleet-page'
import { CopyValue } from '../../../../../../shared/sso-workspace/copy-value'
import { FleetGrid, useGridList } from '../../../../../../shared/sso-workspace/fleet-grid'
import { formatAgo, formatDateTime } from '../../../../../../shared/sso-workspace/format'
import { TableAgClient } from '../../../../../../shared/table-builder/table-ag-client'
import { ROW_HEIGHT_TWO_LINE } from '../../../../../../shared/table-builder/table-ag'
import { ColumnFormat, ColumnMenu } from '../../../../../../shared/table-builder/table-columns'
import { Menu, MenuItem } from '../../../../../../shared/menu/menu'
import { IconActivate, IconActivityLog, IconSsoConnection, IconUnactivate } from '../../../../../../shared/icon/icon'
import { ErrorReference } from '../../../../../../shared/error/error-reference'
import { ScimActivity } from '../../../../../../shared/scim/scim-activity'
import { useImpersonating } from '../../../../../../shared/scim/use-impersonating'
import { useScimActions } from '../../../../../../shared/scim/scim-dialogs'
import { ScimSetupGuide } from '../../../../../../shared/scim/scim-setup-guide'
import {
  baseURLOf, expiryText, GOOGLE_NOTE, linkActions, linkMethod, linkState, madeByText, METHOD_OAUTH, methodText, NOT_CONFIGURED_NOTE, PLAN_OFF_NOTE,
  PUBLIC_NOTE, READ_ONLY_IMPERSONATING, tokenPrefixText
} from '../../../../../../shared/scim/scim-words'

// Provisioning (SCIM) for one connection, staff's view (spec R22): one row
// per linked organization -- On or Off, the token's prefix, its expiry and
// last use, the last request received -- with the base URL to copy (the
// same for every organization: the token names the organization, R2).
// Staff turn provisioning on for a link (the token shows to the
// organization as made by EducationXR support), revoke it, and read what
// the identity provider did (recent events and the latest failed requests,
// never bodies). Staff never rotate: content answers 400, so they revoke
// and turn on again.
//
// links: content's GET /scim/connections/:id/links (undefined while it
// loads, null if it failed; loadFailure is the error behind that). A
// public connection never gets provisioning; Google Workspace gets a note
// (Google provisions only to apps in its own catalog); a stack without
// SCIM_PUBLIC_BASE_URL says it isn't configured here.
export const SectionProvisioning = ({ providerID, links, loadFailure, onLinksChanged, canEdit, providerPublic }) => {
  const impersonating = useImpersonating()
  const [activityFor, setActivityFor] = useState(null) // the organization whose activity is open
  const tableRef = useRef()
  const actions = useScimActions({ staff: true, onChanged: onLinksChanged })

  const rows = useMemo(() => (Array.isArray(links)
    ? links.map(l => {
      const state = linkState(l)
      return {
        ...l,
        id: l.organization.id,
        organizationName: l.organization.name,
        stateKey: state.key,
        stateText: state.label,
        stateTone: state.tone,
        planOff: l.planOn === false,
        tokenText: tokenPrefixText(l.token),
        methodText: methodText(l),
        clientId: linkMethod(l) === METHOD_OAUTH && l.oauth ? l.oauth.clientId : null,
        madeBy: madeByText(l.token),
        expiresText: expiryText(l.token),
        expiresMs: l.token && l.token.expiresAt ? Date.parse(l.token.expiresAt) : Infinity,
        lastUsedAt: l.token ? l.token.lastUsedAt : null
      }
    })
    : links), [links])
  const gridList = useGridList(rows)

  // The row menu may hold handlers from an earlier render: it calls the
  // current ones through this ref.
  const handlers = useRef({})
  handlers.current = {
    turnOn: link => actions.askTurnOn(link),
    revoke: link => actions.askRevoke(link),
    activity: link => setActivityFor(current => (current === link.organization.id ? null : link.organization.id))
  }

  const allPublic = providerPublic || (Array.isArray(links) && links.length > 0 && links.every(l => linkState(l).key === 'public'))
  if (allPublic) return <Notice tone="info">{PUBLIC_NOTE}</Notice>

  const list = Array.isArray(links) ? links : []
  const google = list.some(l => l.connection && l.connection.google)
  const notConfigured = list.some(l => l.reason === 'not_configured' || l.configured === false)
  const baseURL = baseURLOf(list)
  const open = activityFor === null ? null : list.find(l => l.organization.id === activityFor) || null
  const permitted = { canEdit, impersonating, staff: true }

  return (
    <div className="d-grid gap-3">
      {google && <Notice tone="info">{GOOGLE_NOTE}</Notice>}
      {notConfigured && <Notice tone="warn">{NOT_CONFIGURED_NOTE}</Notice>}
      {impersonating && canEdit && <Notice tone="warn">{READ_ONLY_IMPERSONATING}</Notice>}
      {baseURL &&
        <div style={{ maxWidth: 720 }}>
          <CopyValue id="sw-scim-base" label="Tenant URL (SCIM base URL)" value={baseURL}
            hint="The same for every organization linked to this connection: each organization's identity provider gets its own token." />
        </div>}

      {links === undefined && <p className="sw-hint m-0">Loading provisioning…</p>}
      {links === null &&
        <div role="alert">
          <p className="text-danger small m-0">Provisioning couldn't be loaded.</p>
          <ErrorReference error={loadFailure} compact />
        </div>}
      {Array.isArray(links) &&
        <FleetGrid tableRef={tableRef} exportName="scim-provisioning" compact flush title="provisioning by organization" rowHeight={ROW_HEIGHT_TWO_LINE}>
          <TableAgClient
            ref={tableRef}
            queryKey="scim"
            apiList={gridList}
            sortCol="organizationName"
            rowHeight={ROW_HEIGHT_TWO_LINE}
            emptyState={{ Icon: IconSsoConnection, title: 'No organizations linked yet', message: 'Link an organization in Domains & organizations, then turn provisioning on for it here.' }}>
            <ColumnFormat field="organizationName" header="Organization" render={orgCell} stacked sortable />
            <ColumnFormat field="stateText" header="Provisioning" render={stateCell} stacked fit="content" sortable />
            <ColumnFormat field="methodText" header="Authentication" render={methodCell} stacked fit="content" sortable />
            <ColumnFormat field="tokenText" header="Token" render={tokenCell} stacked fit="content" sortable />
            <ColumnFormat field="expiresText" header="Expires" render={expiresCell} fit="content" sortable />
            <ColumnFormat field="lastUsedAt" header="Last used" render={agoCell} exportValue={r => r.lastUsedAt || ''} fit="content" sortable />
            <ColumnFormat field="lastRequestAt" header="Last request" render={agoCell} exportValue={r => r.lastRequestAt || ''} fit="content" sortable />
            <ColumnMenu
              menu={row => {
                const can = linkActions(row, permitted)
                return (
                  <Menu small sentenceCase>
                    <MenuItem Icon={IconActivate} text="turn on" pin show={can.turnOn} onClick={() => handlers.current.turnOn(row)} />
                    <MenuItem Icon={IconActivityLog} text="activity" pin show onClick={() => handlers.current.activity(row)} />
                    <MenuItem Icon={IconUnactivate} text="revoke token" show={can.revoke} onClick={() => handlers.current.revoke(row)} />
                  </Menu>
                )
              }}
            />
          </TableAgClient>
        </FleetGrid>}

      {open &&
        <div className="sw-edit-row" data-testid="scim-activity-row">
          <div className="sw-inline-panel" role="region" aria-label={`Activity for ${open.organization.name}`}>
            <div className="sw-subh">
              <h3>Activity for {open.organization.name}</h3>
              <button type="button" className="btn btn-sm sw-btn" onClick={() => setActivityFor(null)}>Close</button>
            </div>
            <ScimActivity providerID={providerID} organizationID={open.organization.id} />
          </div>
        </div>}

      <ScimSetupGuide />
      {actions.dialogs}
    </div>
  )
}

const orgCell = cell => {
  const row = cell.data
  if (!row) return ''
  return (
    <span className="min-w-0">
      <span className="fw-semibold">{row.organizationName}</span>
      {row.connection && row.connection.ownedByOrganization && <span className="sw-cell-sub">Owns this connection</span>}
    </span>
  )
}

const stateCell = cell => {
  const row = cell.data
  if (!row) return ''
  const live = row.stateKey === 'on' || row.stateKey === 'expiring'
  return (
    <span className="min-w-0">
      <Pill tone={row.stateTone}>{row.stateText}</Pill>
      {row.planOff && !live && <span className="sw-cell-sub" title={PLAN_OFF_NOTE}>SSO plan off</span>}
    </span>
  )
}

// How the link's identity provider authenticates (spec R26), with OAuth's
// client ID.
const methodCell = cell => {
  const row = cell.data
  if (!row || !row.methodText) return ''
  return (
    <span className="min-w-0">
      <span>{row.methodText}</span>
      {row.clientId && <span className="sw-cell-sub sw-mono">{row.clientId}</span>}
    </span>
  )
}

const tokenCell = cell => {
  const row = cell.data
  if (!row) return ''
  if (!row.tokenText) return <span className="sw-hint">None</span>
  return (
    <span className="min-w-0">
      <span className="sw-mono">{row.tokenText}</span>
      {row.madeBy && <span className="sw-cell-sub">by {row.madeBy}</span>}
    </span>
  )
}

const expiresCell = cell => {
  const row = cell.data
  if (!row || !row.token) return ''
  return row.token.expiresAt
    ? <span title={formatDateTime(row.token.expiresAt)}>{row.expiresText}</span>
    : <Pill tone="warn" title="A token that never expires works until someone revokes it.">Never</Pill>
}

const agoCell = cell => (cell.value ? <span title={formatDateTime(cell.value)}>{formatAgo(cell.value)}</span> : <span className="sw-hint">Never</span>)
